import {useMemo, useState} from 'react';
import type {FormDefinition} from './types';
import type {FeedbackFormState, QuestionAnswer} from './state';
import {initialFeedbackState, validateFeedback} from './state';

// Owns the feedback form state, its per-question update handlers, and inline
// validation — keeping this logic out of the JSX.
export function useFeedbackFormState(
  definition: FormDefinition,
  prior?: Partial<FeedbackFormState>,
) {
  const [state, setState] = useState<FeedbackFormState>(() =>
    initialFeedbackState(definition, prior),
  );
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(
    () => validateFeedback(definition, state),
    [definition, state],
  );

  const update = (
    questionId: string,
    updater: (answer: QuestionAnswer) => QuestionAnswer,
  ) => setState((prev) => ({...prev, [questionId]: updater(prev[questionId])}));

  return {
    state,
    errors,
    submitted,

    // Switching the choice drops any note from the prior option so a follow-up
    // explanation can't be attributed to a different answer (re-picking the
    // same option keeps it).
    setSingle: (questionId: string, value: string) =>
      update(questionId, (answer) =>
        answer.kind === 'single'
          ? {...answer, value, note: answer.value === value ? answer.note : ''}
          : answer,
      ),
    setSingleNote: (questionId: string, note: string) =>
      update(questionId, (answer) =>
        answer.kind === 'single' ? {...answer, note} : answer,
      ),
    // An exclusive option (e.g. "Nothing") clears the others and vice versa
    toggleMulti: (questionId: string, value: string, exclusiveValue?: string) =>
      update(questionId, (answer) => {
        if (answer.kind !== 'multi') return answer;
        if (value in answer.selections) {
          const selections = {...answer.selections};
          delete selections[value];
          return {...answer, selections};
        }
        if (value === exclusiveValue) {
          return {...answer, selections: {[value]: ''}};
        }
        const selections = {...answer.selections};
        if (exclusiveValue !== undefined) delete selections[exclusiveValue];
        selections[value] = '';
        return {...answer, selections};
      }),
    setMultiNote: (questionId: string, value: string, note: string) =>
      update(questionId, (answer) =>
        answer.kind === 'multi'
          ? {...answer, selections: {...answer.selections, [value]: note}}
          : answer,
      ),
    setRating: (questionId: string, value: number) =>
      update(questionId, (answer) =>
        answer.kind === 'rating' ? {...answer, value} : answer,
      ),
    setText: (questionId: string, text: string) =>
      update(questionId, (answer) =>
        answer.kind === 'text' ? {...answer, text} : answer,
      ),

    // Reveal inline errors and, when clean, hand back the collected state.
    submit: (): FeedbackFormState | null => {
      setSubmitted(true);
      return Object.keys(errors).length === 0 ? state : null;
    },
  };
}

export type FeedbackFormApi = ReturnType<typeof useFeedbackFormState>;
